export type Action<T> = { type: T | null; [key: string]: any };
export type Reducer<T, A> = (prevState: T | undefined, action: Action<A>) => T;
export type ContextData<T, A> = [T, React.Dispatch<Action<A>>];

export enum CHECKOUT_PAGE {
  DELIVERY = 'DELIVERY',
  PAYMENT = 'PAYMENT'
}

export enum COUNTRY_ISO {
  GB = 'GB',
  IE = 'IE',
  IM = 'IM'
}

export enum DELIVERY_ADDRESS_TYPE {
  DELIVERY,
  COLLECT,
  NO_DELIVERY_TYPE
}

export enum BILLING_ADDRESS_TYPE {
  NEW_ADDRESS = 'NEW_ADDRESS',
  SAME_AS_DELIVERY = 'SAME_AS_DELIVERY'
}

export interface Country {
  name: string;
  isocode: string;
}

export enum DeliveryOption {
  STD = 'STD',
  NDD = 'NDD',
  NOD = 'NOD',
  ROI = 'ROI'
}

export enum DeliveryCollectOption {
  RLC = 'RLC',
  DTS = 'DTS'
}
/**
 * Amplience image transformation templates
 *
 * https://confluence.tacitknowledge.com/display/BB/Existing+Hybris+Media+Formats+to+Transformation+Template+mapping
 *
 * @export
 * @enum {number}
 */
export enum ImageTransformation {
  /**
   * "Product List Page" image
   */
  LOUPLP = 'LOUPLP',
  /**
   * "Product List Page" Retina image
   */
  LOUPLP_R = 'LOUPLP_R',
  /**
   * "Product Details Page" large image
   */
  LOUPDP_L = 'LOUPDP_L',
  /**
   * "Product Details Page" large Retina image
   */
  LOUPDP_L_R = 'LOUPDP_L_R',
  /**
   * "Product Details Page" main product image
   */
  LOUPDP_M = 'LOUPDP_M',
  /**
   * "Product Details Page" main product Retina image
   */
  LOUPDP_M_R = 'LOUPDP_M_R',
  /**
   * "Product Details Page" thumbnail image
   */
  LOUPDP_T = 'LOUPDP_T',
  /**
   * "Product Details Page" thumbnail Retina image
   */
  LOUPDP_T_R = 'LOUPDP_T_R',
  /**
   * "Delivery summary" product image
   */
  LOUDEL_SUM = 'LOUDEL_SUM',
  /**
   * "Delivery summary" Retina product image
   */
  LOUDEL_SUM_R = 'LOUDEL_SUM_R',
  /**
   * "Order summary" product image
   */
  LOUORD_SUM = 'LOUORD_SUM',
  /**
   * "Order summary" Retina product image
   */
  LOUORD_SUM_R = 'LOUORD_SUM_R',
  /**
   * "Cart" product image
   */
  LOUCART = 'LOUCART',
  /**
   * "Cart" Retina product image
   */
  LOUCART_R = 'LOUCART_R',
  /**
   * "Reserve & Collect Details Page" product image
   */
  LOURESERVE_DETAILS = 'LOURESERVE_DETAILS',
  /**
   * "Reserve & Collect Details Page" Retina product image
   */
  LOURESERVE_DETAILS_R = 'LOURESERVE_DETAILS_R',
  /**
   * "Cart: Free Gift" product image
   */
  LOUFG_SELECT = 'LOUFG_SELECT',
  /**
   * "Cart: Free Gift" Retina product image
   */
  LOUFG_SELECT_R = 'LOUFG_SELECT_R',
  /**
   * Attraqt search: popular products suggestions image
   */
  LOUATSSG = 'LOUATSSG',
  /**
   * Attraqt search: popular products suggestions Retina image
   */
  LOUATSSG_R = 'LOUATSSG_R',
  /**
   * New header: Mini favourites 95px
   */
  LOUNHMF = 'LOUNHMF',
  /**
   * New header: Mini favourites 190px
   */
  LOUNHMF_R = 'LOUNHMF_R',
  /**
   * New header: Mini cart 95px
   */
  LOUNHMC = 'LOUNHMC',
  /**
   * New header: Mini cart 190px
   */
  LOUNHMC_R = 'LOUNHMC_R',
  /**
   * New PLP: Product card 300px
   */
  LOUNPLP = 'LOUNPLP',
  /**
   * New PLP: Product card 600px
   */
  LOUNPLP_R = 'LOUNPLP_R',
  /**
   * New PLP: Favourites banner 50px
   */
  LOUNPLP_ATF = 'LOUNPLP_ATF',
  /**
   * New PLP: Favourites banner 100px
   */
  LOUNPLP_ATF_R = 'LOUNPLP_ATF_R',
  /**
   * Checkout: confirmation page
   */
  LOUCHECK = 'LOUCHECK',
  /**
   * Checkout: confirmation page retina
   */
  LOUCHECK_R = 'LOUCHECK_R'
}

export interface ImageTransformationTemplateEntity {
  [key: number]: ImageTransformation | ImageTransformation[] | string | string[];
}

export interface DeliveryOptionData {
  name: string;
  costData: {
    currencyIso: string;
    formattedValue: string;
    value: number;
  };
  countries: Country[];
  currentDatetime: string;
}

export interface DeliveryOptionNDD extends DeliveryOptionData {
  deliveryDate: string;
  hoursLeftToThreshold: number;
  showCountdown: boolean;
  thresholdHour: number;
}

export interface DeliveryOptionNOD extends DeliveryOptionData {
  available: boolean;
  excludedDates: string[];
  maxDays: number;
}

export interface DeliverToMe {
  [DeliveryOption.STD]?: DeliveryOptionData;
  [DeliveryOption.ROI]?: DeliveryOptionData;
  [DeliveryOption.NDD]?: DeliveryOptionNDD;
  [DeliveryOption.NOD]?: DeliveryOptionNOD;
}

interface ClickCollect {
  [DeliveryCollectOption.RLC]?: DeliveryOptionData;
  [DeliveryCollectOption.DTS]?: DeliveryOptionData;
}

export type DeliveryOptions = DeliverToMe & ClickCollect;

interface CustomerDetails {
  titleCode?: string | null;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface FormAddressData extends CustomerDetails {
  countryIso: string;
  county?: string;
  line1: string;
  line2?: string;
  postcode: string;
  postcodeLookup?: string;
  townCity: string;
}

export interface AddressData extends FormAddressData {
  deliveryAddress?: boolean;
  deliveryDate?: null;
  id: string;
  paymentAddress?: boolean;
  type?: null;
}

export interface ClickCollectFormData {
  [DeliveryCollectOption.RLC]: boolean;
  [DeliveryCollectOption.DTS]: boolean;
  geolocationLookup: string;
  deliveryAddressLine?: string;
  storeId?: number;
}

export interface ClickCollectLocation {
  id: number;
  name: string;
  type: DeliveryCollectOption;
  openingHours: {
    weekDayOpeningList: {
      weekDay: string;
      openingTime: { formattedHour: string };
      closingTime: { formattedHour: string };
    }[];
  };
  address: string;
  countryIso: string;
  formattedDistance: string;
  line1: string;
  line2: string;
  postcode: string;
  townCity: string;
  collectionTime?: string;
  storeId?: number;
  deliveryAddressLine?: string;
  county: string;
  countryName: string | null;
  logoImageUrl?: string | null;
}

export interface UserTitle {
  title: string;
  titleCode: string;
}

export interface Price {
  formattedValue: string | null;
  value: number | null;
}

interface FreeGift {
  code: string;
  imageUrl: string;
  name: string;
  price: Price;
  quantity: number;
  url: string;
}

export interface CartEntryData {
  productCode: string;
  fhProductCode: string;
  name: string;
  nameWithBrand: string;
  nameWithoutBrand: string;
  sku: string;
  skuForDataLayer: string;
  skuCategory: string;
  manufacturer: string;
  variant: boolean;
  baseProductCode: string;
  baseProductName: string;
  productVariant: string;
  url: string;
  totalWasPrice: Price;
  totalWasWasPrice: Price;
  totalPrice: Price;
  basePrice: Price;
  deliveryModeCode: DeliveryOption | DeliveryCollectOption;
  primaryImageUrl: string;
  deliveryDate: {
    deliveryModeCode: DeliveryOption;
    date: string;
  }[];
  displayCode: string;
  quantity: number;
  maxQuantity: number;
  size: null | string;
  wristSize: null | number;
  wristSizes: number[];
  ring: boolean;
  availableFreeGifts: FreeGift[];
  selectedFreeGift: FreeGift | null;
  freeGift: boolean;
}

export enum API {
  GET_LOGGED_IN_USER = '/api/v1/auth',
  LOGIN = '/api/v1/login',
  VALIDATE_NEW_CUSTOMER = '/api/v1/validateNewCustomer',
  REGISTER_AND_LOGIN = '/api/v1/loupe/registerNewCustomerAndLogin',
  LOGIN_EXISTS = '/api/v1/loginExists',
  RESET_PASSWORD = '/api/v1/checkout/resetPassword',
  CAPTCHA_REQUIRED = '/captcha/required',
  COMMUNICATIONS = '/api/v1/account/communications',
  CONFIRM_CART = '/api/v1/checkout/cart/confirm',
  ADDRESS_LOOKUP = '/address-lookup/ajax/address-details',
  RESEND_ACTIVATION_EMAIL = '/login/resendAccountEmailAjax',
  GET_DELIVERY_OPTIONS = '/api/v1/checkout/cart/deliveryOptions',
  UPDATE_DELIVERY_OPTION = '/api/v1/checkout/updateDeliveryMode',
  GET_USER_TITLES = '/api/v1/user/titles',
  GET_COUNTRIES = '/api/v1/checkout/payment/countries',
  V12_FINANCE_DETAILS_PDP = '/api/v1/finance/v12Details/product',
  V12_FINANCE_DETAILS_CART = '/api/v1/checkout/cart/finance/v12Details',
  V12_FINANCE_DETAILS_CHECKOUT = '/api/v1/checkout/finance/v12Details',
  HITACHI_FINANCE_DETAILS_PDP = '/api/v1/finance/novunaDetails/product',
  HITACHI_FINANCE_DETAILS_CART = '/api/v1/checkout/cart/finance/novunaDetails',
  HITACHI_FINANCE_DETAILS_CHECKOUT = '/api/v1/checkout/finance/novunaDetails',
  GET_ADDRESSES = '/api/v1/checkout/account/addresses',
  SAVE_DELIVERY_ADDRESS = '/api/v1/checkout/delivery/address',
  SAVE_DELIVERY_ADDRESS_DTS = '/api/v1/checkout/delivery/dtsAddress',
  SAVE_DELIVERY_ADDRESS_RLC = '/api/v1/checkout/delivery/rlcAddress',
  GET_COLLECT_STORES = '/api/v1/checkout/delivery/locations',
  SAVE_PAYMENT_ADDRESS = '/api/v1/checkout/payment/address',
  SUBMIT_GIFT_MESSAGE = '/api/v1/checkout/delivery/giftMessage',
  GET_CARD_FLEX_KEY = '/api/v1/checkout/payment/cybs/cc/getFlexKey',
  GET_CHECKOUT_DATA = '/api/v1/checkout',
  GET_CART_DATA = '/api/v1/checkout/cart',
  ERROR_ON_MICROFORM_SETUP = '/checkout/cybersource/errorOnMicroformSetup',
  ERROR_ON_CARD_PAYMENT = '/checkout/cybersource/errorOnTokenCreation',
  ERROR_ON_CARDINAL_SETUP = '/checkout/cybersource/errorOnCardinalSetup',
  ERROR_ON_CARDINAL_CONTINUE = '/checkout/cybersource/errorOnCardinalContinue',
  ERROR_ON_CARDINAL_BIN_PROFILING = '/checkout/cybersource/errorOnCardinalBinProfiling',
  ERROR_ON_CARDINAL_PAYMENTS_VALIDATED = '/checkout/cybersource/errorOnCardinalPaymentsValidated',
  CREATE_CARDINAL_JWT = '/checkout/cybersource/createCardinalJWT',
  PLACE_CARD_ORDER = '/api/v1/checkout/payment/cybs/cc/initPayment',
  CYBERSOURCE_VALIDATE = '/api/v1/checkout/payment/cybs/cc/validatePayment',
  GET_POSTCODE_ADDRESSES = '/api/v1/checkout/delivery/lookupAddresses',
  GET_FULL_POSTCODE_ADDRESS = '/address-lookup/ajax/details',
  PLACE_PAYPAL_ORDER = '/api/v1/checkout/payment/paypal/placeOrder',
  PLACE_V12_FINANCE_ORDER = '/api/v1/checkout/payment/finance/placeOrder',
  PLACE_HITACHI_FINANCE_ORDER = '/api/v1/checkout/payment/novuna/placeOrder',
  ADD_GIFT_CARD = '/api/v1/checkout/payment/giftcard/add',
  GET_GIFT_CARD_LIST = '/api/v1/checkout/payment/giftcard/list',
  REMOVE_GIFT_CARD = '/api/v1/checkout/payment/giftcard/remove',
  PLACE_GIFT_CARD_ORDER = '/api/v1/checkout/payment/giftcard/placeOrder',
  PLACE_FREE_PAYMENT_ORDER = '/api/v1/checkout/payment/freeOfCharge/placeOrder',
  CREATE_KLARNA_SESSION = '/api/v1/checkout/payment/klarna/createSession',
  UPDATE_KLARNA_SESSION = '/api/v1/checkout/payment/klarna/updateSession',
  PLACE_ORDER_WITH_KLARNA = '/api/v1/checkout/payment/klarna/placeOrder',
  ERROR_ON_KLARNA_INIT = 'api/v1/checkout/payment/klarna/errorOnInit',
  MOVE_TO_FAVOURITES = '/api/v1/user/favourites/add',
  CHANGE_QUANTITY = '/api/v1/checkout/cart/entries/',
  CHANGE_WRIST_SIZE = '/api/v1/checkout/cart/entries/',
  CHANGE_FREE_GIFT = '/api/v1/checkout/cart/entries/[productCode]/freeGift/[index]',
  APPLY_VOUCHER = '/api/v1/checkout/cart/redeemVoucher',
  RELEASE_VOUCHER = '/api/v1/checkout/cart/releaseVoucher',
  UPDATE_PAYMENT_MODE = '/api/v1/checkout/cart/updatePaymentMode',
  GET_RECENTLY_VIEWED_PRODUCTS = '/api/v1/checkout/cart/recentlyViewedProducts',
  MESSAGES = '/messages',
  PRODUCT_RESERVATION = '/api/v1/reservation/stores',
  RESERVATION_PAGE = '/reservation/details/page',
  ADD_TO_CART = '/bag/add',
  ADD_GIFTCARD_TO_CART = '/bag/addgiftcard',
  PLACE_RESERVATION = '/api/v1/reservation/place',
  CONFIRM_APPOINTMENT = '/consultations/confirm',
  GUEST_CONFIRM_APPOINTMENT = '/consultations/guestConfirm',
  PRODUCT_VARIANTS = '/api/v1/product/[productCode]/variants',
  FAVOURITES_ROLLOVER = '/favourites/rollover',
  FAVOURITES_ADD = '/favourites/add',
  FAVOURITES_REMOVE = '/favourites/remove',
  CART_ROLLOVER = '/bag/rollover',
  SEARCH_ATTRAQT = '/api/v1/shopping/attraqt/search',
  SUGGESTIONS_ATTRAQT = '/api/v1/shopping/search/suggestions',
  SEARCH = '/search',
  CATEGORY_ATTRAQT = '/api/v1/shopping/attraqt/category/:categoryCode'
}

export interface ErrorBodyResponse {
  errorType: string;
  errorMessage: string;
}

export enum StorageStateKey {
  GLOBAL_MESSAGES = 'GLOBAL_MESSAGES'
}

export enum CheckoutStorageStateKey {
  CLOSE_BAG_URL = 'CLOSE_BAG_URL',
  SELECTED_PAYMENT_TYPE = 'SELECTED_PAYMENT_TYPE',
  SELECTED_DELIVER_TO_ME_MODE = 'SELECTED_DELIVER_TO_ME_MODE',
  SELECTED_DELIVER_TO_ME_DATE = 'SELECTED_DELIVER_TO_ME_DATE',
  NR_OF_SAVED_ADDRESSES_TO_SHOW = 'NR_OF_SAVED_ADDRESSES_TO_SHOW',
  CART_GUID = 'CART_GUID',
  FINANCE_DATA = 'FINANCE_DATA',
  FINANCE_PAYMENT_URL = 'FINANCE_PAYMENT_URL',
  FINANCE_PAYMENT_TYPE = 'FINANCE_PAYMENT_TYPE',
  ACTIVE_PAGE = 'ACTIVE_PAGE',
  LOGIN_FORM = 'LOGIN_FORM',
  LOGIN_EXISTS = 'LOGIN_EXISTS',
  LOGIN_DISABLE_PREFERENCES = 'LOGIN_DISABLE_PREFERENCES',
  LOGIN_FORGOT_PASSWORD_TOGGLE = 'LOGIN_FORGOT_PASSWORD_TOGGLE',
  LOGIN_FORGOT_PASSWORD_SENT = 'LOGIN_FORGOT_PASSWORD_SENT',
  LOGIN_FORGOT_PASSWORD_FORM = 'LOGIN_FORGOT_PASSWORD_FORM',
  DELIVERY_TYPE = 'DELIVERY_TYPE',
  DELIVERY_ADDRESS = 'DELIVERY_ADDRESS',
  DELIVERY_SELECTED_DELIVERY_MODE = 'DELIVERY_SELECTED_DELIVERY_MODE',
  DELIVERY_SELECTED_SAVED_ADDRESS = 'DELIVERY_SELECTED_SAVED_ADDRESS',
  DELIVERY_ENTERED_FORM_ADDRESS = 'DELIVERY_ENTERED_FORM_ADDRESS',
  DELIVERY_ADDRESS_FORM = 'DELIVERY_ADDRESS_FORM',
  HITACHI_BILLING_ADDRESS_FORM = 'HITACHI_BILLING_ADDRESS_FORM',
  CLICK_COLLECT_FORM_DATA = 'CLICK_COLLECT_FORM_DATA',
  CLICK_COLLECT_SEARCH_TERM = 'CLICK_COLLECT_SEARCH_TERM',
  CLICK_COLLECT_FOUND_LOCATIONS = 'CLICK_COLLECT_FOUND_LOCATIONS',
  CLICK_COLLECT_FILTERED_LOCATIONS = 'CLICK_COLLECT_FILTERED_LOCATIONS',
  CLICK_COLLECT_NEAREST_STORE = 'CLICK_COLLECT_NEAREST_STORE',
  CLICK_COLLECT_SELECTED_STORE = 'CLICK_COLLECT_SELECTED_STORE',
  CLICK_COLLECT_DELIVERY_DATES = 'CLICK_COLLECT_DELIVERY_DATES',
  CLICK_COLLECT_LIMIT_STORES_DISPLAY = 'CLICK_COLLECT_LIMIT_STORES_DISPLAY',
  GIFT_IS_GIFT_ORDER = 'GIFT_IS_GIFT_ORDER',
  GIFT_MESSAGE_BOX = 'GIFT_MESSAGE_BOX',
  GIFT_MESSAGE_VALUE = 'GIFT_MESSAGE_VALUE',
  GIFT_MESSAGE_ERROR = 'GIFT_MESSAGE_ERROR',
  GIFT_MESSAGE_CHARS = 'GIFT_MESSAGE_CHARS',
  BILLING_ADDRESS_ID = 'BILLING_ADDRESS_ID',
  BILLING_ADDRESS_FORM = 'BILLING_ADDRESS_FORM',
  DELIVERY_SET_ADDRESS_MANUALLY = 'DELIVERY_SET_ADDRESS_MANUALLY',
  HITACHI_BILLING_SET_ADDRESS_MANUALLY = 'HITACHI_BILLING_SET_ADDRESS_MANUALLY',
  BILLING_SET_ADDRESS_MANUALLY = 'BILLING_SET_ADDRESS_MANUALLY',
  DELIVERY_POST_CODE_ADDRESS = 'DELIVERY_POST_CODE_ADDRESS',
  DELIVERY_BILLING_POST_CODE_ADDRESS = 'DELIVERY_BILLING_POST_CODE_ADDRESS',
  BILLING_POST_CODE_ADDRESS = 'BILLING_POST_CODE_ADDRESS',
  ORDER_SUMMARY_PANEL = 'ORDER_SUMMARY_PANEL'
}

export enum ListerStorageStateKey {
  IS_BIGGER_IMAGES_SELECTED = 'IS_BIGGER_IMAGES_SELECTED'
}

export enum PaymentType {
  GIFT_CARD = 'giftcard',
  CARD = 'creditcard',
  PAYPAL = 'paypal',
  V12 = 'v12',
  HITACHI = 'hitachi',
  FREE = 'free',
  NO_PAYMENT_TYPE = ''
}

export enum CardType {
  VISA = 'visa',
  VISA_DEBIT = 'visa-debit',
  MASTERCARD = 'mastercard',
  AMEX = 'amex'
}

export interface GiftCard {
  cardNumber: string;
  amountRedeemed: string;
  amountRemaining: string;
}

export interface PaymentMode {
  code: PaymentType;
  excludedDeliveryModes: (DeliveryOption | DeliveryCollectOption)[];
  cartPromotionData: {
    highBoundary: number;
    lowBoundary: number;
    message: string;
  } | null;
  paymentPromotionData: {
    message: string;
  } | null;
}

export interface PaymentProps {
  selectedPayment: PaymentType;
  onPaymentSelect: (paymentType: PaymentType) => void;
}

export interface CheckoutPaymentProps extends PaymentProps {
  isPlacingOrder: boolean;
  isAddressPushing: boolean;
  setIsPlacingOrder: (isPlacingOrder: boolean) => void;
  promotionData?: {
    message: string;
  } | null;
  cartPromotionData?: {
    lowBoundary?: number;
    highBoundary?: number;
    message?: string;
  } | null;
  showFinanceNotice?: boolean;
}

export enum FinanceTermTypes {
  DeferredInterestFreeCredit = 'DEFERREDINTERESTFREECREDIT',
  InterestFreeCredit = 'INTERESTFREECREDIT',
  InterestBearingCredit = 'INTERESTBEARINGCREDIT'
}

interface FinanceTerm {
  apr: number;
  code: string;
  instalmentMultiplier: number;
  term: number;
}

export interface HitachiFinanceTerm extends FinanceTerm {
  defaultDeposit: number;
  defaultDepositAmount: number;
  deferredPeriod: number;
  fixedDepositAmount: number;
  maxDeposit: number;
  maxDepositAmount: number;
  minDeposit: number;
  minDepositAmount: number;
  minLoanAmount: number;
  monthlyRepayment?: number;
  type: FinanceTermTypes;
  yield: number;
}

export interface V12FinanceTerm extends FinanceTerm {
  defaultDeposit: number;
  defaultDepositAmount: number;
  deferredPeriod: number;
  fixedDepositAmount: number;
  maxDeposit: number;
  maxDepositAmount: number;
  minDeposit: number;
  minDepositAmount: number;
  minLoanAmount: number;
  type: FinanceTermTypes;
  yield: number;
}

export interface FinanceProperties {
  monthlyRepayment: number;
  creditAmount: number;
  interestPayable: number;
  totalPayable: number;
}

interface FinanceData {
  isPaymentDeferred: boolean;
  priceValue: number | null;
  presentationalMonthlyRepayment: number | null;
  financeProperties: FinanceProperties | null;
}

export interface V12FinanceData extends FinanceData {
  depositValue: number | null;
  mappedDepositValue: number | null;
  setDepositValue: (depositValue: number) => void;
  selectedTerm: V12FinanceTerm | null;
  setSelectedTerm: (selectedTerm: V12FinanceTerm) => void;
  isFixedDepositSelected: boolean;
  setIsFixedDepositSelected: (isFixedDepositSelected: boolean) => void;
  setIsPaymentDeferred: (isPaymentDeferred: boolean) => void;
  fixedDepositAmount: number | null;
  termsInRage: V12FinanceTerm[];
  deferredTerm: V12FinanceTerm | null;
  maxDeposit: number | null;
  financeType: PaymentType.V12;
}

export interface HitachiFinanceData extends FinanceData {
  selectedTerm: HitachiFinanceTerm | null;
  setSelectedTerm: (selectedTerm: HitachiFinanceTerm) => void;
  setIsPaymentDeferred: (isPaymentDeferred: boolean) => void;
  termsInRage: HitachiFinanceTerm[];
  deferredTerm: HitachiFinanceTerm | null;
  financeType: PaymentType.HITACHI;
  depositValue: number | null;
  mappedDepositValue: number | null;
  depositEnabled: boolean;
  setDepositValue: (depositValue: number) => void;
  isFixedDepositSelected: boolean;
  setIsFixedDepositSelected: (isFixedDepositSelected: boolean) => void;
  fixedDepositAmount: number | null;
  maxDeposit: number | null;
}

export interface GlobalMessage {
  message: string;
  type: string;
}

export interface AddressErrorMessage {
  message: string;
  field: string;
}

export interface SelectedSavedAddressErrors {
  id: string;
  errorMessages: AddressErrorMessage[];
}

export interface MessageProperties {
  [key: string]: string;
}

export interface FormattedMessageType {
  id: string;
  values?: {
    [key: number]: string | number;
  };
  fallback?: string;
}

export type FormattedMessageRenderer = ({ id, values, fallback }: FormattedMessageType) => string;

export enum ProductDimensionTypes {
  SWATCH = 'SWATCH',
  STRAP_TYPE = 'STRAP_TYPE',
  CASE_SIZE = 'CASE_SIZE',
  DIAMOND_CARAT_WEIGHT = 'DIAMOND_CARAT_WEIGHT'
}

export enum ProductDimensionsProductTypePrefixes {
  WATCH = 'watch',
  WATCH_RANGE = 'watchrange',
  RING = 'ring',
  BRACELET = 'bracelet',
  EARRING = 'earring',
  NECKLACE = 'necklace',
  ANKLET = 'anklet',
  BANGLE = 'bangle'
}

export enum VariantType {
  CaseSize = 'CaseSize',
  RingSize = 'RingSize',
  EarringSize = 'EarringSize',
  BraceletLength = 'BraceletLength',
  NecklaceLength = 'NecklaceLength'
}

export type ProductDimensionShape = {
  code: string;
  name: string;
  colourSwatch: {
    code: string;
    name: string;
    tooltip: string | null;
    label: string | null;
    labelColour: string | null;
    colours: string[];
    swatchUrl: string;
  } | null;
  url: string;
  image: string;
  enabled: boolean;
  price: PriceData;
  wasPrice: PriceData;
  wasWasPrice: PriceData;
  priceUponEnquiry: boolean;
  variantType: VariantType;
  variantOptions: { value: string }[];
};

export type ProductDimensionValue = {
  value: string;
  products: ProductDimensionShape[];
};

export type ProductDimension = {
  type: ProductDimensionTypes;
  values: ProductDimensionValue[];
  tooltip: string | null;
};

export type ProductDimensions = ProductDimension[];

export interface ReserveCollectSearchResultStore {
  instoreAvailability: {
    available: boolean;
    message: string;
  };
  onlineAvailability: {
    available: boolean;
    message: string;
  };
  availableInstoreProduct: {
    code: string;
    size: string | null;
  };
  store: {
    address: {
      line2: string;
    };
    formattedDistance: string;
    storeId: number;
    storeName: string;
  };
}

export interface SetItem {
  src: string;
  type: string;
}

export interface ViewerMediaItem {
  src: string;
  type: 'img' | 'set' | 'video';
  name?: string;
  items?: SetItem[];
  model?: boolean;
}

export interface MediaSet {
  src: null;
  name: string;
  type: string;
  status?: string;
  items: ViewerMediaItem[];
}

export interface ReservationData {
  intendedProductCode: string | null;
  product: {
    code: string | null;
    imageUrl: string;
    name: string;
    price: null;
    productUrl: string;
    size: string | null;
  };
  store: {
    id: number;
    address: string;
    name: string;
    scheduleMessage: string;
    weWillHoldYourItemsMessage: string;
  };
}

export interface ReservationConfirmationData {
  code: string;
  contactInfo: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    title: string;
  };
  entries: {
    price: Price;
    product: {
      code: string | null;
      imageUrl: string;
      name: string;
      productUrl: string;
    };
  }[];
  reserveEndDate: string;
  store: {
    addressLine1: string;
    name: string;
    postalCode: string;
    storeUrl: string;
    town: string;
  };
}

export interface AppointmentConfirmationBrand {
  id: string;
  name: string;
}

export interface InterestedProductData {
  title: string;
  productUrl: string;
  imageUrl: string;
  code: string;
  price: string;
  variant: string | null;
}

export interface AppointmentData {
  productInitiatorSku: string;
  date: string;
  slotName: string;
  storeData: {
    address: {
      line1: string;
      line2: string;
      postalCode: string;
    };
    name: string;
  };
  type: {
    code: string;
    name: string;
  };
}

export interface CurrentCustomerData {
  name: string;
  phone: string;
  contactEmail: string;
}

export interface PriceData {
  currencyIso: string;
  formattedValue: string;
  maxQuantity: null;
  minQuantity: null;
  priceType: string;
  seoFormattedValue: string;
  value: number;
}

export interface ProductVariant {
  availableForPurchase: boolean;
  eligibleForFinance: boolean;
  eligibleForNdd: boolean;
  formatVariant: string;
  priceData: PriceData;
  productCode: string;
  title: string;
}

export interface CybsConfiguration {
  timeoutInMs?: number;
  extendedTimeoutInMs?: number;
  cardinalLogLevel?: string;
  cardinalSetupTimeoutInMs?: number;
  cardinalPaymentValidateTimeoutInMs?: number;
}

export interface MiniEntry {
  fhProductCode: string;
  sku: string;
  skuForDataLayer: string;
  skuCategory: string;
  name: string;
  nameWithBrand: string;
  manufacturer: string;
  productCode: string;
  baseProductCode: string;
  baseProductName: string;
  variant: boolean;
  price: number;
  productUrl: string;
  productImage: string;
  productTitle: string;
  productPrice: string;
  productWasPrice: string;
  productWasWasPrice: string;
  productVariant: string;
  productDiscounted: boolean;
  productUnavailable: boolean;
  priceUponEnquiry: boolean;
}

export interface MiniCartEntry extends MiniEntry {
  productQuantity: number;
  productVariants: string[];
}

export interface MiniFavouritesData {
  entries: MiniEntry[];
  miniFavouritesDisplayLimit: number;
}

export interface MiniCartData {
  entries: MiniCartEntry[];
  miniCartDisplayLimit: number;
  totalEntryQuantities: number;
  subtotal: string;
}

export interface AddToCartData extends MiniCartData {
  entry: MiniCartEntry;
  errorMessage: string;
}

export interface FacetValueData {
  code: string;
  count: number;
  disabled: boolean;
  name: string;
  query: { location: string };
  selected: boolean;
}

export interface FacetData {
  code: string;
  name: string;
  expanded: boolean;
  facetId: string;
  values: FacetValueData[];
  disabled: boolean;
  priceFacet: boolean;
  multiSelect: boolean;
  priceHigher?: number;
  priceLower?: number;
  selectedPriceHigher?: number;
  selectedPriceLower?: number;
}

export interface RolexCollectionLink {
  url: string;
  name: string;
}

export type ProductStatusOnListerPage =
  | 'OutOfStock'
  | 'Archived'
  | 'Unavailable'
  | 'StoreOnly'
  | 'ComingSoon'
  | 'Info'
  | 'PriceUponEnquiry'
  | ''
  | null;

export type PriceSliderStyle = 'linear' | 'logarithmic';

export type Sash = 'Sale' | 'Saving' | 'Offer' | '' | null;

export interface ListerProductData {
  amplienceRoundel: string | null;
  availableColorSwatches: string[] | null;
  badges: string[] | null;
  code: string;
  imageUrl: string | null;
  baseImageUrl: string | null;
  manufacturer: string | null;
  minIfcPayment: string | null;
  financingPromoMessage: string | null;
  name: string;
  price: PriceData | null;
  productStatusOnListerPage: ProductStatusOnListerPage;
  purchasableWebStatus: boolean;
  retailOnly: 'Y' | 'N' | null;
  discountMessage: string | null;
  sash: Sash;
  sku: string;
  thumbUrl: string | null;
  url: string | null;
  wasPrice: PriceData | null;
  wasWasPrice: PriceData | null;
  webProductStatus: 'LiveActive' | 'DiscontinuedActive' | '' | null;
  modelCase: string | null;
}

export interface ListerSortData {
  code: string;
  name: string;
  selected: boolean;
}

export interface BreadcrumbData {
  disabled: boolean;
  facetCode: string;
  facetName: string;
  facetValueCode: string;
  facetValueName: string;
  facetValueNameMobile: string;
  priceFacet: boolean;
  removeQuery: {
    location: string;
  };
  location: string;
  truncateQuery: null;
}

export interface SuggestionData {
  suggestion: string;
  query: {
    location: string;
  };
}

export interface ListerData {
  pagination: {
    pageSize: number;
    sort: string | null;
    currentPage: number;
    totalNumberOfResults: number;
    numberOfPages: number;
  };
  breadcrumbs: BreadcrumbData[];
  results: ListerProductData[];
  rid: string;
  sorts: ListerSortData[];
  currentQuery: {
    location: string;
    freeTextSearch: string;
    pageSize: null;
    categoryCode: null;
    sort: string | null;
    currentPage: number;
  };
  facets: FacetData[];
  rolexCollectionLinks?: RolexCollectionLink[];
  previousFacets: FacetData[];
  previousResults: ListerProductData[];
  spellingSuggestion?: SuggestionData;
  categoryCode: string | null;
}

export interface AttraqtSuggestionsProduct {
  manufacturer: string | null;
  code: string | null;
  name: string | null;
  price: string | null;
  wasPrice: string | null;
  imageUrl: string | null;
  url: string | null;
  productStatusOnListerPage: string | null;
}

export interface AttraqtSuggestionsCategory {
  code: string | null;
  name: string | null;
  location: string | null;
}

export interface AttraqtSuggestionsSearchTerm {
  term: string | null;
  nrResults: number | null;
}

export interface AttraqtSuggestionsResponse {
  message: string;
  status: string;
  successfull: boolean;
  data: {
    suggestion: {
      searchTerms: AttraqtSuggestionsSearchTerm[];
      products: AttraqtSuggestionsProduct[];
      categories: AttraqtSuggestionsCategory[];
    };
  };
}

export interface PurchasedProduct {
  attraqt: {
    fhProductCode: string;
  };
  name: string;
  baseProductName: string;
  nameWithBrand: string;
  sku: string;
  skuForDataLayer: string;
  skuCategory: string;
  manufacturer: string;
  productCode: string;
  variant: boolean;
  baseProductCode: string;
  productVariant: string;
  currency: string;
  quantity: number;
  price: number;
  promotions: string;
  priceType: string;
  category: string;
  deliveryTime: string;
}

export enum LayoutTypes {
  IMAGE_PER_ROW = '1',
  TWO_IMAGES_PER_ROW = '2'
}

export interface ZoomedImage {
  src: string;
  index: number;
  height: number;
  width: number;
}

export type AbGroup = {
  code?: string;
  campaignId?: string;
  abGroupFinance?: string;
} | null;

export interface SelectedVariant {
  variant: string;
  productCode: string;
  fhProductCode: string;
  sku: string;
  skuForDataLayer: string;
  skuCategory: string;
  price: number;
  name: string;
  baseProductName: string;
  manufacturer: string;
  nameWithBrand: string;
  productVariant: string;
}
